import * as React from 'react';
import { useState } from 'react';
import {
  Avatar,
  Box,
  Typography,
  Stack,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, axiosInstance } from '../services/apiClient';
import { UserCreateDTO } from '../generated/api-client';
import toast from 'react-hot-toast';
import PasswordRequirementsChecklist from './PasswordRequirementsChecklist';
import { describeMissingRequirements, validatePassword } from '../utils/passwordPolicy';

export interface Account {
  id: number;
  name: string;
  email: string;
  image?: string;
  color?: string;
  projects?: Array<{ id: number; title: string }>;
}

export interface AccountPreviewProps {
  handleClick?: () => void;
  open?: boolean;
  account?: Account;
}

export const AccountPreview: React.FC<AccountPreviewProps> = ({
  handleClick,
  open = true,
  account,
}) => {
  const theme = useTheme();
  const { user, logout } = useAuth();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firstname, setFirstname] = useState(user?.firstname || '');
  const [lastname, setLastname] = useState(user?.lastname || '');
  const [changePassword, setChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  React.useEffect(() => {
    if (user) {
      setFirstname(user.firstname || '');
      setLastname(user.lastname || '');
    }
  }, [user]);

  const getInitials = (firstname: string, lastname: string) => {
    return `${firstname[0]}${lastname[0]}`;
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleSettings = () => {
    handleMenuClose();
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    setChangePassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setNewPasswordConfirm('');
    if (user) {
      setFirstname(user.firstname || '');
      setLastname(user.lastname || '');
    }
  };

  const handleSaveSettings = async () => {
    if (!user?.id) return;

    if (changePassword) {
      if (!currentPassword || !newPassword || !newPasswordConfirm) {
        toast.error('Compila tutti i campi password');
        return;
      }
      if (newPassword !== newPasswordConfirm) {
        toast.error('Le nuove password non coincidono');
        return;
      }
      const missingRequirements = describeMissingRequirements(newPassword);
      if (missingRequirements) {
        toast.error(missingRequirements);
        return;
      }
    }

    setLoading(true);
    try {
      // Aggiorna nome e cognome
      const updateDto = new UserCreateDTO({
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        email: user.email,
      });
      await apiClient.update_item_user__item_id__put(user.id, updateDto);

      // Se necessario, cambia password
      if (changePassword) {
        await axiosInstance.post('/auth/change-password', {
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirm: newPasswordConfirm,
        });
      }

      toast.success('Profilo aggiornato con successo');
      handleSettingsClose();
      // Ricarica la pagina per aggiornare i dati utente
      window.location.reload();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiornamento del profilo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        onClick={handleMenuClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          p: open ? 1.5 : 1,
          cursor: 'pointer',
          transition: theme.transitions.create(['padding', 'justify-content'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Avatar
          sx={{
            width: open ? 40 : 32,
            height: open ? 40 : 32,
            bgcolor: 'primary.main',
            fontSize: open ? '1rem' : '0.875rem',
            mr: open ? 1.5 : 0,
            transition: theme.transitions.create(['width', 'height', 'font-size', 'margin-right'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            //color: 'white',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              textAlign: 'center',
            }}
          >
            <span style={{ lineHeight: open ? 1 : 1.5 }}>
              {getInitials(user?.firstname ?? ' ', user?.lastname ?? ' ')}
            </span>
          </Box>
        </Avatar>
        {open && (
          <Stack
            direction="column"
            spacing={0.25}
            sx={{
              flex: 1,
              minWidth: 0,
              opacity: open ? 1 : 0,
              width: open ? 'auto' : 0,
              overflow: 'hidden',
              transition: theme.transitions.create(['opacity', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.firstname} {user?.lastname}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email}
            </Typography>
          </Stack>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Impostazioni</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={settingsOpen} onClose={handleSettingsClose} maxWidth="sm" fullWidth>
        <DialogTitle>Impostazioni Profilo</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome"
              value={firstname}
              onChange={e => setFirstname(e.target.value)}
              fullWidth
            />
            <TextField
              label="Cognome"
              value={lastname}
              onChange={e => setLastname(e.target.value)}
              fullWidth
            />
            {
              <FormControlLabel
                control={
                  <Checkbox
                    checked={changePassword}
                    onChange={e => setChangePassword(e.target.checked)}
                  />
                }
                label="Cambia password"
              />
            }
            {changePassword && (
              <>
                <TextField
                  label="Password attuale"
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Nuova password"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  fullWidth
                />
                {newPassword.length > 0 && <PasswordRequirementsChecklist password={newPassword} />}
                <TextField
                  label="Conferma nuova password"
                  type="password"
                  value={newPasswordConfirm}
                  onChange={e => setNewPasswordConfirm(e.target.value)}
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ pb: 2, pr: 2 }}>
            <Button onClick={handleSettingsClose} disabled={loading}>
              Annulla
            </Button>
            <Button
              sx={{ ml: 2 }}
              onClick={handleSaveSettings}
              variant="contained"
              disabled={
                loading ||
                (changePassword &&
                  (!validatePassword(newPassword).valid || newPassword !== newPasswordConfirm))
              }
            >
              {loading ? <CircularProgress size={24} /> : 'Salva'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};
